import { useDroppable } from "@dnd-kit/core";
import GridViewRoundedIcon from "@mui/icons-material/GridViewRounded";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import TableRowsRoundedIcon from "@mui/icons-material/TableRowsRounded";
import {
  Box,
  Breadcrumbs,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import * as React from "react";
import { Link, useSearchParams } from "react-router";

import { RobotoLinkHoverUnderline } from "@/shared/components";
import { Slider } from "@/shared/foundation/primitives";
import { Goto } from "@/shared/navigation";

import styles from "./DatasetBreadcrumbs.module.css";
import { CRUMB_DROP_PREFIX } from "./FileBrowserDndContext";
import { TriggerEvaluationStatus } from "./TriggerEvaluationStatus";

interface DatasetBreadcrumbsProps {
  datasetId: string;
  filePaths: string | undefined;
  displayMode: string;
  gridItems: number;
  handleDisplayToggle: (mode: string) => void;
  handleGridSliderChange: (gridItems: number) => void;
  sliderValueFormat: (value: number) => string;
}

interface DroppableCrumbProps {
  label: string;
  to: string;
  dropId: string;
  currentPath: string;
}

const DroppableCrumb: React.FC<DroppableCrumbProps> = ({
  label,
  to,
  dropId,
  currentPath,
}) => {
  const [searchParams] = useSearchParams();
  const crumbPath = dropId.slice(CRUMB_DROP_PREFIX.length);
  const isCurrentDir = crumbPath === currentPath;

  const { isOver, setNodeRef } = useDroppable({
    id: dropId,
    disabled: isCurrentDir,
  });

  return (
    <Tooltip title="Move here" open={isOver} placement="bottom" arrow>
      <span
        ref={setNodeRef}
        className={`${styles.crumb}${isOver ? ` ${styles["crumb-hot"]}` : ""}`}
      >
        <RobotoLinkHoverUnderline
          variant="body2"
          to={`${to}?${searchParams.toString()}`}
        >
          {label}
        </RobotoLinkHoverUnderline>
      </span>
    </Tooltip>
  );
};

export const DatasetBreadcrumbs: React.FC<DatasetBreadcrumbsProps> = ({
  datasetId,
  displayMode,
  gridItems,
  handleDisplayToggle,
  handleGridSliderChange,
  sliderValueFormat,
  filePaths = "",
}) => {
  const theme = useTheme();

  const files =
    filePaths.length > 0
      ? filePaths.split("/").filter((item) => item.length > 0)
      : [];

  const currentPath = filePaths;

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
      }}
    >
      <Box
        sx={{
          display: "flex",
          gap: theme.spacing(0.5),
          alignItems: "center",
        }}
      >
        <Breadcrumbs
          maxItems={4}
          itemsAfterCollapse={2}
          itemsBeforeCollapse={1}
          separator={<NavigateNextIcon fontSize="small" />}
        >
          <Link
            key={"datasets-link"}
            to={Goto.route.Datasets}
            style={{ textDecoration: "none" }}
          >
            <Typography
              variant="body2"
              sx={{
                color: theme.palette.primary.main,
                ":hover": { textDecoration: "underline" },
              }}
            >
              Datasets
            </Typography>
          </Link>

          {/* Dataset root crumb — drop here moves items to dataset root.
              It links to the `files` route directly: the bare dataset URL
              is an index route that redirects here, and passing through it
              remounts the browser and drops the current file selection. */}
          <DroppableCrumb
            label={datasetId}
            to={`/datasets/${datasetId}/files`}
            dropId={`${CRUMB_DROP_PREFIX}`}
            currentPath={currentPath}
          />

          {files.map((item, index, filePathArray) => {
            const crumbPath = filePathArray.slice(0, index + 1).join("/");
            return (
              <DroppableCrumb
                key={crumbPath}
                label={item}
                to={`/datasets/${datasetId}/files/${crumbPath}`}
                dropId={`${CRUMB_DROP_PREFIX}${crumbPath}`}
                currentPath={currentPath}
              />
            );
          })}
        </Breadcrumbs>
      </Box>

      <Box
        sx={{
          gap: theme.spacing(1),
          cursor: "pointer",
          display: {
            xs: "none",
            sm: "flex",
          },
          alignItems: "center",
        }}
      >
        <TriggerEvaluationStatus
          className={styles["trigger-evaluation-status"]}
          datasetId={datasetId}
        />
        <Box sx={{ display: "flex", gap: theme.spacing(1) }}>
          {displayMode === "grid" && (
            <div className={styles.zoomSlider}>
              <Slider
                value={gridItems}
                min={1}
                step={1}
                max={12}
                onValueChange={handleGridSliderChange}
                valueLabel={sliderValueFormat}
                aria-label="Grid zoom"
              />
            </div>
          )}

          <ToggleButtonGroup
            color="primary"
            exclusive
            onChange={(_, newView: string) => {
              handleDisplayToggle(newView);
            }}
            size="small"
            value={displayMode}
          >
            <ToggleButton value={"table"} aria-label="table-view">
              <TableRowsRoundedIcon fontSize="small" />
            </ToggleButton>
            <ToggleButton value={"grid"} aria-label="grid-view">
              <GridViewRoundedIcon fontSize="small" />
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Box>
    </Box>
  );
};
